/**
 * useActivityContext — hold contextIds on the org's activity socket for the
 * lifetime of a component (TASK-7922504E, batch contract C-12).
 *
 * The ids are retained on mount and released on unmount or when the SET of ids
 * changes (a new array with the same ids re-renders without touching the
 * wire). Frames keep flowing through `EVENT_TYPE_MAP` exactly as before;
 * `onEvent` is an optional add-on for a component that wants the matching
 * frames themselves.
 *
 * Returns the authorisation status per contextId as a record that keeps its
 * identity until one of those statuses actually changes. Signed out (no
 * socket) every id reads `pending`.
 */

import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react'

import { useWebSocketStatus } from '@/providers/webSocketContext'
import type { ActivityHandler } from './activityClient'
import { PENDING_STATUS, type ActivitySocket, type ContextStatus } from './activitySocket'

/** Status per held contextId. */
export type ActivityContextStatuses = Readonly<Record<string, ContextStatus>>

const EMPTY_IDS: readonly string[] = []
const noopUnsubscribe = (): void => {}
const subscribeNothing = (): (() => void) => noopUnsubscribe

/** One key per SET of ids, so array identity and order never re-retain. */
const keyOf = (contextIds: readonly string[]): string => [...new Set(contextIds)].sort().join('\n')

/** A snapshot reader that returns the previous record while no status changed. */
function createSnapshotReader(socket: ActivitySocket | null, ids: readonly string[]): () => ActivityContextStatuses {
  let cached: ActivityContextStatuses | null = null
  return () => {
    const statusOf = (contextId: string): ContextStatus => socket?.statusOf(contextId) ?? PENDING_STATUS
    if (cached !== null) {
      const previous = cached
      if (ids.every((contextId) => previous[contextId] === statusOf(contextId))) return previous
    }
    const next: Record<string, ContextStatus> = {}
    for (const contextId of ids) next[contextId] = statusOf(contextId)
    cached = Object.freeze(next)
    return cached
  }
}

export function useActivityContext(
  contextIds: readonly string[],
  onEvent?: ActivityHandler
): ActivityContextStatuses {
  const { socket } = useWebSocketStatus()
  const key = keyOf(contextIds)
  const ids = useMemo(() => (key === '' ? EMPTY_IDS : key.split('\n')), [key])

  useEffect(() => {
    if (socket === null || ids.length === 0) return
    return socket.retain(ids)
  }, [socket, ids])

  // Latest-handler ref: a new `onEvent` identity per render never re-registers.
  const handlerRef = useRef(onEvent)
  useEffect(() => {
    handlerRef.current = onEvent
  })
  const wantsEvents = onEvent !== undefined
  useEffect(() => {
    if (socket === null || !wantsEvents || ids.length === 0) return
    return socket.onContextEvent(ids, (event) => handlerRef.current?.(event))
  }, [socket, ids, wantsEvents])

  const subscribe = socket === null ? subscribeNothing : socket.subscribeStatus
  const getSnapshot = useMemo(() => createSnapshotReader(socket, ids), [socket, ids])
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

export default useActivityContext
